/**
 * Blocking-overlay registry for canvas keyboard shortcuts (RFC: overlay-aware
 * Delete guard).
 *
 * Lives in `hooks/` (a neutral shared module) rather than under `canvas-v2/` so
 * shared UI primitives like `ui/Modal` — used by auth/chat/consent/settings too
 * — can register without depending on canvas feature internals.
 *
 * Problem this solves: canvas keyboard shortcuts (Delete/Backspace node
 * deletion, Cmd+C/V/D/Z, Tab) are handled by a global `window` keydown listener
 * in WorkspacePageV2 (and a parallel document capture-phase handler in
 * GroupNode). Those handlers only guarded on the focused element's tag
 * (INPUT/TEXTAREA/contentEditable), so a full-screen overlay portaled to
 * `document.body` (e.g. the audio editor) left the underlying node selected and
 * a stray Delete/Backspace deleted the *covered* node.
 *
 * Fix: overlays that visually cover / capture the canvas register themselves as
 * "blocking" while open. The shortcut handlers bail unconditionally while any
 * blocking overlay is open — independent of focus, which is not a reliable
 * signal here (the shared Modal and the bespoke portals do not move focus into
 * themselves on open). When no blocking overlay is open, behavior is unchanged
 * (rubber-band multi-select, body focus, inline canvas text fields all work).
 *
 * The registry is a simple ref-counted module-level counter so nested overlays
 * (e.g. StyleLibraryModal -> StyleLightbox) compose correctly and each cleanup
 * is idempotent (safe under React StrictMode's double-invoke of effects).
 */
import { useEffect } from 'react'

let blockingOverlayCount = 0

/**
 * Mark a blocking overlay as open. Returns an idempotent release function;
 * calling it more than once only decrements the counter once.
 */
export function pushBlockingOverlay(): () => void {
  blockingOverlayCount += 1
  let released = false
  return () => {
    if (released) return
    released = true
    blockingOverlayCount = Math.max(0, blockingOverlayCount - 1)
  }
}

/** True while at least one blocking overlay is open. */
export function isBlockingOverlayOpen(): boolean {
  return blockingOverlayCount > 0
}

/**
 * Test-only reset so suites can start from a known state regardless of prior
 * mount/unmount ordering.
 */
export function __resetBlockingOverlaysForTest(): void {
  blockingOverlayCount = 0
}

/**
 * Register the calling component as a blocking overlay while `active` is true.
 * Auto-releases on unmount or when `active` becomes false. Components that only
 * render while open can pass `useBlockingOverlay(true)`.
 */
export function useBlockingOverlay(active: boolean): void {
  useEffect(() => {
    if (!active) return
    return pushBlockingOverlay()
  }, [active])
}
